import React, { ReactNode, useState } from 'react';
import type {
  BasePatcherProps,
  WithPassedPatchableGroupProps
} from './PatchableGroup';
import {
  Checkbox,
  CheckboxProps,
  FormControlLabel,
  FormControlLabelProps,
  Switch,
  SwitchProps
} from '@mui/material';

export type CheckboxPatcherProps = BasePatcherProps &
  Omit<SwitchProps, keyof BasePatcherProps> &
  Omit<CheckboxProps, keyof BasePatcherProps> & {
    compType?: 'switch' | 'checkbox';
    label?: ReactNode;
    labelPlacement?: FormControlLabelProps['labelPlacement'];
  };

const CheckboxPatcherComponent = <T,>({
  /* eslint-disable @typescript-eslint/no-unused-vars */
  compType = 'switch',
  patchable,
  label,
  labelPlacement = 'top',
  wrapper,
  name,
  error,
  loading,
  sourceType,
  hideLoadingOverlay,
  /* eslint-enable @typescript-eslint/no-unused-vars */
  ...restProps
}: WithPassedPatchableGroupProps<T, CheckboxPatcherProps>) => {
  const [instantVal, setInstantVal] = useState<boolean>(
    typeof patchable?.currentValue === 'boolean'
      ? patchable.currentValue
      : !!restProps.defaultChecked
  );

  if (!patchable) {
    throw new Error(
      'A Patcher component must be used as a child of a PatchableGroup'
    );
  }

  const doPatch = (v: boolean) => {
    if (patchable) {
      const { onPatch } = patchable;
      onPatch({ [name]: v });
    }
  };

  const Component = compType === 'switch' ? Switch : Checkbox;

  return (
    <FormControlLabel
      control={
        <Component
          color="primary"
          {...restProps}
          checked={instantVal}
          readOnly={patchable.readOnly}
          onChange={(e, v) => {
            setInstantVal(v);
            doPatch(v);
          }}
        />
      }
      label={label}
      labelPlacement={labelPlacement}
      disabled={loading}
    />
  );
};

export const CheckboxPatcher = (props: CheckboxPatcherProps) => (
  <CheckboxPatcherComponent {...props} />
);
